import { EntityType, ProjectScope, ProjectType } from '@crew/enums/domain'
import { generateImageAvatarUrl } from '@crew/utils/avatar'
import { getDefaultTabValue } from '@crew/utils/enum'
import classNames from 'classnames'
import {
  CrewAvatar,
  CrewAvatarShapeType,
  CrewAvatarSize,
} from 'components/elements/crewAvatar/crewAvatar'
import { CrewUserAvatar } from 'components/elements/crewUserAvatar/crewUserAvatar'
import { DirectMessageTabs, GroupChatTabs, ProjectDetailTabs } from 'enums/app'
import { FC, memo, useMemo } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { CrewChatRoomUnreadBadge } from 'components/elements/crewChatRoomUnreadBadge/crewChatRoomUnreadBadge'
import { ChatRoom } from '@crew/apis/chat/models/searchChatRooms/response'

export type SearchChatRoomResultListItemProps = {
  chatRoom: SearchChatRoom
}

export type SearchChatRoom = ChatRoom

// メニューの基本スタイル
const baseClassNames =
  'flex flex-row h-full w-full items-center px-2 py-1.5 gap-2 hover:font-bold [&:has(.has-unread)]:font-bold group-hover:font-bold'

// 選択メニューのアクティブ状態スタイル（ハイライト）
const activeClassNames = 'font-bold crew-text-gray-1 crew-bg-gray-2'

// チャットルーム検索結果のアイテム
export const SearchChatRoomResultListItem: FC<SearchChatRoomResultListItemProps> =
  memo((props) => {
    // クリック時のナビゲーション先
    const location = useLocation()

    // URLの変更に応じてハイライトさせるかどうかをハンドリングする
    const isActive = useMemo(
      () =>
        // eg: location.pathname = /projects/{projectId}/xxx
        // projectIdのみで判定する。projectIdで一意に識別出来るので、他の条件は不要
        location.pathname.split('/')[2] === props.chatRoom.projectId,
      [location, props.chatRoom.projectId]
    )

    // クリック時のナビゲーション先
    const navLink = useMemo(() => {
      if (props.chatRoom.projectType === ProjectType.Project) {
        return `/${EntityType.Project}/${
          props.chatRoom.projectId
        }/${getDefaultTabValue(ProjectDetailTabs)}`
      } else if (props.chatRoom.projectType === ProjectType.DirectChannel) {
        return `/direct-channels/${props.chatRoom.projectId}/${getDefaultTabValue(
          DirectMessageTabs
        )}`
      } else if (props.chatRoom.projectType === ProjectType.InstantChannel) {
        return `/group-chats/${props.chatRoom.projectId}/${getDefaultTabValue(
          GroupChatTabs
        )}`
      }

      // projectTypeが適切でないことは通常あり得ないが、NavLinkのtoがundefinedを取れないため空文字を返すようにしておく
      return ''
    }, [props.chatRoom.projectType, props.chatRoom.projectId])

    return (
      <li
        key={props.chatRoom.projectId}
        className="flex flex-row group relative justify-between"
      >
        <NavLink
          to={navLink}
          className={classNames(baseClassNames, isActive && activeClassNames)}
        >
          <div className="flex">
            <div className="flex w-full items-center">
              {/* アバターを描画 */}
              {props.chatRoom.projectType === ProjectType.Project && (
                // 通常プロジェクトのアバター
                <CrewAvatar
                  displayName={props.chatRoom.projectName}
                  size={CrewAvatarSize.xs}
                  shape={CrewAvatarShapeType.Square}
                  imageURL={generateImageAvatarUrl(
                    EntityType.Project,
                    props.chatRoom.projectId
                  )}
                  isPrivate={props.chatRoom.scope === ProjectScope.Private}
                  cacheValue={props.chatRoom.projectId + props.chatRoom.version}
                />
              )}
              {props.chatRoom.projectType === ProjectType.DirectChannel &&
                props.chatRoom.directChannelAnotherUser && (
                  // ダイレクトチャンネルのアバター
                  // ダイレクトチャンネルの場合は、付加情報であるdirectChannelAnotherUserを使用してアバターを表示する
                  <CrewUserAvatar
                    userId={props.chatRoom.directChannelAnotherUser.id}
                    directChannelId={props.chatRoom.projectId}
                    displayName={props.chatRoom.projectName}
                    size={CrewAvatarSize.xs}
                    cacheValue={
                      props.chatRoom.directChannelAnotherUser.id +
                      props.chatRoom.directChannelAnotherUser.version
                    }
                    showPresenceState={true}
                  />
                )}
              {props.chatRoom.projectType === ProjectType.InstantChannel && (
                // グループチャットのアバター
                // CREW-21003で正式に実装するため、現状は暫定実装
                // https://break-tmc.atlassian.net/browse/CREW-21003
                <CrewAvatar
                  cacheValue={props.chatRoom.projectId + props.chatRoom.version}
                  displayName={props.chatRoom.projectName}
                  shape={CrewAvatarShapeType.Circle}
                  imageURL={generateImageAvatarUrl(
                    EntityType.Project,
                    props.chatRoom.projectId
                  )}
                  size={CrewAvatarSize.xs}
                />
              )}

              {/* 選択中 または 未読ありの場合、太字で表示 */}
              {props.chatRoom.projectType === ProjectType.InstantChannel ? (
                // グループチャットの場合、メンバー数を合わせて表示
                <span className="line-clamp-2 ml-2 mr-1 break-all">
                  {props.chatRoom.projectMemberCount
                    ? `${props.chatRoom.projectName} (${props.chatRoom.projectMemberCount})`
                    : props.chatRoom.projectName}
                </span>
              ) : (
                <span className="line-clamp-2 ml-2 mr-1 break-all">
                  {props.chatRoom.projectName}
                </span>
              )}
              <CrewChatRoomUnreadBadge chatRoomId={props.chatRoom.chatRoomId} />
            </div>
          </div>
        </NavLink>
      </li>
    )
  })
